import { Link } from "@inertiajs/react"
import { LinkExternal01 } from "@untitledui/icons"
import { format } from "date-fns"

import { reasonLabel } from "@/components/calls/copy"
import { Badge, StatusBadge } from "@/components/ui/badge"
import { cx } from "@/components/ui/cx"
import { Panel } from "@/components/ui/panel"
import { mmss } from "@/lib/duration"

import type { Contact, LoggedCall, Writeback } from "../types"

const formatStamp = (iso: string) =>
  format(new Date(iso), "d MMM yyyy 'at' HH:mm")

/** Where the Salesforce write got to: written with a link, pending, or why it stopped. */
export function WritebackBadge({ writeback }: { writeback: Writeback | null }) {
  if (!writeback) {
    return (
      <Badge color="gray" size="sm">
        Not sent to Salesforce
      </Badge>
    )
  }
  switch (writeback.state) {
    case "written":
      return writeback.salesforce_url ? (
        <a
          href={writeback.salesforce_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex"
        >
          <StatusBadge tier="remote">
            In Salesforce <LinkExternal01 className="ml-1 size-3" />
          </StatusBadge>
        </a>
      ) : (
        <StatusBadge tier="remote">In Salesforce</StatusBadge>
      )
    case "pending":
      return (
        <StatusBadge tier="system">
          {writeback.attempts > 0
            ? `Retrying (${writeback.attempts})`
            : "Sending"}
        </StatusBadge>
      )
    case "rejected":
      return <StatusBadge tier="caution">Not sent</StatusBadge>
    case "paused":
      return <StatusBadge tier="caution">Not sent</StatusBadge>
  }
}

/** The mono meta line: who was reached, how long the talk and the memo were, which attempt. */
const voiceMeta = (c: LoggedCall) =>
  [
    c.reached ? `reached ${c.reached.replace("_", " ")}` : null,
    c.voice?.talk_seconds != null ? `talk ${mmss(c.voice.talk_seconds)}` : null,
    c.voice?.memo_seconds != null
      ? `memo ${mmss(c.voice.memo_seconds)}${c.voice.memo_recovered ? " (called back)" : ""}`
      : null,
    c.attempt ? `attempt ${c.attempt} of 5` : null,
  ]
    .filter(Boolean)
    .join(" · ")

/**
 * The calls logged against this school, newest first, each with its
 * Salesforce state. A voice call is the same row grammar as a hand-logged one
 * plus three additions: memo length in the mono meta, Summary and Transcript
 * disclosures inline, and the Task badge. A call still to be reviewed, or one
 * the system flagged, sits in the same list so nothing is invisible.
 */
export function LoggedCallsPanel({
  calls,
  contacts,
}: {
  calls: LoggedCall[]
  contacts: Contact[]
}) {
  const nameOf = (id: string | null) =>
    contacts.find((c) => c.id === id)?.name ?? null
  return (
    <Panel
      title="Logged calls"
      description="Every call logged here is a Task on the account in Salesforce, owned by whoever logged it."
      headingLevel="h3"
    >
      {calls.length === 0 ? (
        <p className="text-tertiary text-sm">Nothing logged yet.</p>
      ) : (
        <ul className="divide-secondary flex flex-col divide-y">
          {calls.map((c) => {
            const v = c.voice
            const unlogged =
              v && (v.review_state === "ready" || v.review_state === "flagged")
            const flagged = v?.review_state === "flagged"
            return (
              <li
                key={c.id}
                className={cx(
                  "flex flex-col gap-1.5 py-3",
                  flagged && "caution-band -mx-3.5 px-3.5",
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-primary text-sm font-medium">
                    {unlogged
                      ? "Voice call — not yet logged"
                      : (c.disposition_label ?? "Logged")}
                  </span>
                  {!unlogged && c.attempt && !c.spoke && (
                    <span className="text-tertiary text-xs">
                      attempt {c.attempt} of 5
                    </span>
                  )}
                  {flagged && v && (
                    <StatusBadge tier="caution">
                      {reasonLabel(v.flag_reason, v.flag_reason_label)}
                    </StatusBadge>
                  )}
                  {!unlogged && <WritebackBadge writeback={c.writeback} />}
                </div>
                <p className="text-tertiary text-xs">
                  {formatStamp(c.at)}
                  {c.by ? ` · ${c.by}` : ""}
                  {nameOf(c.contact_id)
                    ? ` · with ${nameOf(c.contact_id)}`
                    : ""}
                  {c.via_reception ? " · via reception" : ""}
                  {v
                    ? ` · ${voiceMeta(c)}`
                    : c.talk_minutes > 0
                      ? ` · ${c.talk_minutes} min · hand-logged`
                      : " · hand-logged"}
                </p>
                {unlogged && v ? (
                  <p className="text-secondary text-sm">
                    {flagged
                      ? "The agenda marks are kept; an operator can finish this call."
                      : "The summary is ready to review."}
                    {v.review_path && (
                      <>
                        {" "}
                        <Link
                          href={v.review_path}
                          className="text-brand-secondary hover:text-brand-secondary_hover"
                        >
                          {flagged ? "Open this call" : "Review this call"}
                        </Link>
                      </>
                    )}
                  </p>
                ) : (
                  c.notes && (
                    <p className="text-secondary text-sm whitespace-pre-line">
                      {c.notes}
                    </p>
                  )
                )}
                {c.follow_ups.length > 0 && (
                  <ul className="text-secondary text-sm">
                    {c.follow_ups.map((f, i) => (
                      <li key={i}>
                        Follow up: {f.action}
                        {f.when ? ` by ${f.when}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
                {v && !unlogged && (v.summary || v.transcript) && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {v.summary && (
                      <Disclosure label="Summary">{v.summary}</Disclosure>
                    )}
                    {v.transcript && (
                      <Disclosure label="Transcript">{v.transcript}</Disclosure>
                    )}
                  </div>
                )}
                {c.writeback?.attention_line && (
                  <p className="text-caution-700 text-xs">
                    {c.writeback.attention_line}
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Panel>
  )
}

/** An inline disclosure: a mono label that opens the text beneath it. */
function Disclosure({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <details className="group min-w-0 basis-full">
      <summary className="notation hover:text-primary cursor-pointer list-none select-none">
        <span className="group-open:hidden">▸</span>
        <span className="hidden group-open:inline">▾</span> {label}
      </summary>
      <p className="text-secondary mt-1.5 text-sm whitespace-pre-line">
        {children}
      </p>
    </details>
  )
}
